import React, { useState, useEffect } from 'react';
import { Camera, FileText, Car, CheckCircle2, ArrowRight, ArrowLeft, Users, LogOut } from 'lucide-react';
import './App.css';
import { supabase, logAuditEvent } from './lib/supabase';
import { startSyncEngine } from './lib/auditQueue';
import { useAuth } from './lib/AuthContext';
import SyncStatus from './components/SyncStatus';
import Login from './components/Login';
import OrgSetup from './components/OrgSetup';
import InspectionWizard from './components/InspectionWizard';
import ReportGenerator from './components/ReportGenerator';
import UserManagement from './components/UserManagement';

function App() {
  const { user, profile, role, organisation, hasOrg, can, loading: authLoading, signOut } = useAuth();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    window.addEventListener('swUpdate', () => setUpdateAvailable(true));
    startSyncEngine();
    return () => window.removeEventListener('swUpdate', () => setUpdateAvailable(true));
  }, []);
  const [currentStep, setCurrentStep] = useState('start');
  const [inspectionData, setInspectionData] = useState({
    vehicleInfo: {},
    photos: {},
    inspectionResults: {},
    timestamp: null
  });

  const steps = [
    { id: 'vehicle-info', title: 'Vehicle Information', icon: Car },
    { id: 'bol', title: 'BOL Documentation', icon: FileText },
    { id: 'keys', title: 'Keys & FOBs', icon: CheckCircle2 },
    { id: 'function', title: 'Vehicle Function', icon: CheckCircle2 },
    { id: 'documentation', title: 'Documentation', icon: FileText },
    { id: 'exterior', title: 'Exterior Inspection', icon: Camera },
    { id: 'interior', title: 'Interior Inspection', icon: Camera },
    { id: 'engine', title: 'Engine Compartment', icon: Camera },
    { id: 'review', title: 'Final Review', icon: CheckCircle2 }
  ];



  const currentStepIndex = steps.findIndex(step => step.id === currentStep);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const handleStepComplete = async (stepData) => {
    const updatedData = {
      ...inspectionData,
      ...stepData,
      timestamp: inspectionData.timestamp || new Date().toISOString()
    };
    
    setInspectionData(updatedData);

    // Write audit event for every step save
    logAuditEvent({
      inspectionId: updatedData.inspectionId,
      eventType: updatedData.inspectionId && !inspectionData.inspectionId
        ? 'inspection_created'
        : 'step_saved',
      step: currentStep,
      newValue: stepData,
      metadata: { stepIndex: steps.findIndex(s => s.id === currentStep) },
    });


    // Save to Supabase if we have an inspection ID
    if (updatedData.inspectionId) {
      try {
        // Update the inspection in the database
        const updates = {};
        Object.keys(stepData).forEach(key => {
          if (key.includes('_data')) {
            updates[key] = stepData[key];
          }
        });
        
        if (Object.keys(updates).length > 0) {
          await supabase
            .from('vehicle_inspections')
            .update(updates)
            .eq('id', updatedData.inspectionId);
        }
      } catch (error) {
        console.error('Error saving step data:', error);
      }
    }

    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex].id);
    }
  };

  const handlePrevious = () => {
    const prevStepIndex = currentStepIndex - 1;
    if (prevStepIndex >= 0) {
      setCurrentStep(steps[prevStepIndex].id);
    }
  };

  const handleNext = () => {
    const nextStepIndex = currentStepIndex + 1;
    if (nextStepIndex < steps.length) {
      setCurrentStep(steps[nextStepIndex].id);
    }
  };


  // Show nothing while auth is loading
  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  // Gate entire app behind login
  if (!user) return <Login />;

  // Authenticated but not yet assigned to an org — show setup screen
  if (!hasOrg) return <OrgSetup />;

  if (currentStep === 'start') {
    return (
      <div className="app">
        {updateAvailable && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
            background: '#2563eb', color: 'white',
            padding: '0.75rem 1rem',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontSize: '0.9rem', fontWeight: 500,
          }}>
            <span>A new version is available</span>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'white', color: '#2563eb',
                border: 'none', borderRadius: '6px',
                padding: '0.35rem 0.85rem', fontWeight: 600,
                cursor: 'pointer', fontSize: '0.85rem',
              }}
            >
              Update now
            </button>
          </div>
        )}

        <div className="hero-section">
          <div className="hero-content">
            <div className="app-icon">
              <Car className="w-12 h-12 text-blue-600" />
            </div>
            <h1 className="hero-title">Vehicle Condition Report</h1>
            <p className="hero-subtitle">Complete professional vehicle inspections with guided workflows</p>
            
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
              {organisation?.name && (
                <span style={{ display: 'block', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginBottom: '0.2rem' }}>
                  {organisation.name}
                </span>
              )}
              Signed in as <strong style={{ color: 'rgba(255,255,255,0.9)' }}>{profile?.full_name || user?.email}</strong>
              <span style={{
                marginLeft: '0.5rem', padding: '0.1rem 0.5rem',
                background: 'rgba(255,255,255,0.15)', borderRadius: '999px',
                fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize',
              }}>{role}</span>
            </div>

            {can.createInspection && (
              <button
                onClick={() => setCurrentStep('vehicle-info')}
                className="cta-button"
              >
                <Car className="w-5 h-5" />
                Start New Inspection
                <ArrowRight className="w-5 h-5" />
              </button>
            )}

            {can.manageUsers && (
              <button
                onClick={() => setCurrentStep('users')}
                className="cta-button"
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', marginTop: '0.5rem' }}
              >
                <Users className="w-5 h-5" />
                Manage Users
              </button>
            )}

            <button
              onClick={signOut}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
                cursor: 'pointer', fontSize: '0.85rem', marginTop: '1rem',
                fontFamily: 'inherit', padding: '0.5rem',
              }}
            >
              <LogOut style={{ width: 14, height: 14 }} /> Sign out
            </button>

            <div className="feature-grid">
              <div className="feature-card">
                <Camera className="w-6 h-6 text-blue-500" />
                <h3>Guided Photo Capture</h3>
                <p>Step-by-step instructions for consistent documentation</p>
              </div>
              <div className="feature-card">
                <FileText className="w-6 h-6 text-green-500" />
                <h3>Professional Reports</h3>
                <p>Generate detailed condition reports instantly</p>
              </div>
              <div className="feature-card">
                <CheckCircle2 className="w-6 h-6 text-purple-500" />
                <h3>Complete Workflow</h3>
                <p>Comprehensive inspection checklist</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Header with progress */}
      <div className="app-header">
        <div className="header-content">
          <button 
            onClick={handlePrevious}
            disabled={currentStepIndex === 0}
            className="nav-button nav-back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="header-info">
            <h2 className="step-title">{steps[currentStepIndex]?.title}</h2>
            <div className="step-counter">{currentStepIndex + 1} of {steps.length}</div>
          </div>
          <SyncStatus />

          <button 
            onClick={() => setCurrentStep('start')}
            className="nav-button nav-home"
          >
            <Car className="w-5 h-5" />
          </button>
        </div>
        
        <div className="progress-container">
          <div 
            className="progress-bar"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="main-content">
        {currentStep === 'users' ? (
          <UserManagement />
        ) : currentStep === 'review' ? (
          <ReportGenerator inspectionData={inspectionData} />
        ) : (
          <InspectionWizard
            currentStep={currentStep}
            inspectionData={inspectionData}
            onStepComplete={handleStepComplete}
            onNext={handleNext}
            onPrevious={handlePrevious}
          />
        )}
      </div>

      {/* Step navigation */}
      <div className="step-nav">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;
          
          return (
            <div 
              key={step.id}
              className={`step-indicator ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
            >
              {isCompleted ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <Icon className="w-4 h-4" />
              )}
              <span className="step-label">{step.title}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default App;
